import React, { useState, ChangeEvent } from 'react';
import axios from 'axios';
import { X, MinusCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useOverlayClose } from '../../components/useOverlayClose';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const inp: React.CSSProperties = {
  padding: '0.5rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: '6px',
  fontSize: '0.85rem', boxSizing: 'border-box', width: '100%',
};
const lbl: React.CSSProperties = {
  fontSize: '0.75rem', color: '#64748b', fontWeight: 700,
  textTransform: 'uppercase', display: 'block', marginBottom: '0.3rem',
};

interface Props {
  batchId: number;
  currentQuantity: number;
  batchNo: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AdjustStockModal: React.FC<Props> = ({ batchId, currentQuantity, batchNo, onClose, onSuccess }) => {
  const [deductQty, setDeductQty] = useState('');
  const [reason, setReason]       = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlayClose = useOverlayClose(onClose);

  const handleSubmit = async () => {
    const qty = parseInt(deductQty);
    if (!deductQty.trim() || isNaN(qty) || qty < 1) {
      toast.error('Enter a valid quantity to deduct (minimum 1).');
      return;
    }
    if (qty > currentQuantity) {
      toast.error(`Cannot deduct ${qty} — only ${currentQuantity} units available.`);
      return;
    }
    if (!reason.trim()) {
      toast.error('A reason is required.');
      return;
    }

    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    setIsSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/consumables/batches/${batchId}/adjust`,
        { quantity_changed: -qty, reason: reason.trim() },
        { headers },
      );
      toast.success(`Deducted ${qty} unit${qty !== 1 ? 's' : ''} successfully.`);
      onSuccess();
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Adjustment failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const remaining = currentQuantity - (parseInt(deductQty) || 0);

  return (
    <div
      {...overlayClose}
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, backdropFilter: 'blur(4px)' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)', width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.1rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <div style={{ background: '#fef2f2', borderRadius: '0.5rem', padding: '0.4rem', display: 'flex' }}>
              <MinusCircle size={18} color="#dc2626" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>Adjust Stock</h2>
              <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#94a3b8' }}>
                {batchNo ? `Batch: ${batchNo}` : `Batch ID: ${batchId}`}
              </p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X size={18} />
          </button>
        </div>

        {/* Current stock pill */}
        <div style={{ margin: '1rem 1.5rem 0', background: '#f8fafc', borderRadius: '8px', padding: '0.65rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.82rem' }}>
          <span style={{ color: '#64748b', fontWeight: 600 }}>Current Stock</span>
          <span style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{currentQuantity} units</span>
        </div>

        {/* Form */}
        <div style={{ padding: '1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label style={lbl}>
              Quantity to Deduct <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="number"
              min="1"
              max={currentQuantity}
              value={deductQty}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setDeductQty(e.target.value)}
              placeholder={`Max ${currentQuantity}`}
              style={{ ...inp, height: '44px', fontSize: '1.1rem', fontWeight: 700 }}
              autoFocus
            />
            {/* Live remaining preview */}
            {deductQty && !isNaN(parseInt(deductQty)) && (
              <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: remaining < 0 ? '#dc2626' : '#64748b' }}>
                Stock after adjustment:{' '}
                <strong style={{ color: remaining < 0 ? '#dc2626' : remaining <= 5 ? '#d97706' : '#15803d' }}>
                  {remaining} units
                </strong>
              </p>
            )}
          </div>

          <div>
            <label style={lbl}>
              Reason <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Damaged during storage, Expired, Used in procedure…"
              rows={3}
              style={{ ...inp, height: 'auto', resize: 'vertical' as const, lineHeight: 1.5 }}
            />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', padding: '0.9rem 1.5rem', borderTop: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
          <button
            onClick={onClose}
            style={{ padding: '0.55rem 1.1rem', backgroundColor: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{ padding: '0.55rem 1.3rem', backgroundColor: isSubmitting ? '#94a3b8' : '#dc2626', color: 'white', border: 'none', borderRadius: '6px', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontSize: '0.875rem', fontWeight: 700 }}
          >
            {isSubmitting ? 'Saving…' : 'Confirm Deduction'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdjustStockModal;
